import { FormattedMessage, useIntl } from 'react-intl';
import {
    Button,
    Box,
    Heading,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    SimpleGrid,
    Spacer,
} from '@chakra-ui/react';
import { isFirstDayOfMonth } from 'date-fns';
import { Formik, Form } from 'formik';

import { getRequiredSubParameterFields, mapSubParameterFormValues } from '../../../services/batteries';
import { createDateAsUTC, lenientParse } from '../../../services/items';

import ParameterFormField from './ParameterFormField';

const AddSubParameterValueModal = ({ selected, subBatteryId, updateRibbonData, ribbonStaticData, ...rest }) => {
    const intl = useIntl();

    const parameter = ribbonStaticData.parameters.find((p) => p.dbFieldName === selected.dbField);
    const allowNegativeValues = ['DischargingAdder', 'ChargingAdder', 'RTDischargingAdder', 'RTChargingAdder'].includes(
        parameter.value.dbFieldName
    );
    const isPercentOutageReadOnly = ribbonStaticData?.readonlyFields
        ? ribbonStaticData?.readonlyFields.includes('PERCENTOUTAGE')
        : false;

    const formHelpers = {
        startDate: {
            label: intl.formatMessage({ id: 'batteries_timeline_add_modal_start_date' }),
            isDisabled: false,
        },
        endDate: {
            label: intl.formatMessage({ id: 'batteries_timeline_add_modal_end_date' }),
            isDisabled: false,
        },
        peakPeriodId: {
            label: intl.formatMessage({ id: 'batteries_timeline_add_modal_peak_period' }),
            isDisabled: false,
        },
        batteryLevel: {
            label: intl.formatMessage({ id: 'batteries_timeline_add_modal_battery_level' }),
            isDisabled: false,
        },
        // 'Percent outage at {percentOutage} default to 100% for Forced Outages and check readonly'
        percentOutage: {
            label: intl.formatMessage({ id: 'batteries_timeline_add_modal_percent_outage' }),
            isDisabled: isPercentOutageReadOnly,
        },
        value: {
            label: parameter.label + ' ' + intl.formatMessage({ id: 'batteries_timeline_add_modal_value' }),
            isDisabled: false,
            allowNegativeValues,
        },
        productTypeId: {
            label: intl.formatMessage({ id: 'batteries_timeline_add_modal_product_type' }),
            isDisabled: false,
        },
        emptyPlaceholder: '',
    };

    const initialFormValues = mapSubParameterFormValues(selected, rest.metadata.defaultPeakPeriodId);
    const requiredFields = getRequiredSubParameterFields(selected);

    // helper object used to create an array with all field names
    const formFields = Object.entries(initialFormValues).map(([key]) => key);

    const validateForm = (values) => {
        const startDate = validateSiblingDate({
            value: values.startDate,
            siblingValue: values.endDate,
            type: 'start',
        });

        const endDate = validateSiblingDate({
            value: values.endDate,
            siblingValue: values.startDate,
            type: 'end',
        });

        const value = requiredFields.includes('value') && validateRequiredField({ value: values.value });

        const peakPeriodId =
            requiredFields.includes('peakPeriodId') && validateRequiredField({ value: values.peakPeriodId });

        const batteryLevel =
            requiredFields.includes('batteryLevel') && validateRequiredField({ value: values.batteryLevel });

        const percentOutage =
            requiredFields.includes('percentOutage') && validateRequiredField({ value: values.percentOutage });

        const productTypeId =
            requiredFields.includes('productTypeId') && validateRequiredField({ value: values.productTypeId });

        return {
            ...(startDate ? { startDate } : null),
            ...(endDate ? { endDate } : null),
            ...(value ? { value } : null),
            ...(peakPeriodId ? { peakPeriodId } : null),
            ...(productTypeId ? { productTypeId } : null),
            ...(batteryLevel ? { batteryLevel } : null),
            ...(percentOutage ? { percentOutage } : null),
        };
    };

    const validateSiblingDate = ({ value, siblingValue, type }) => {
        if (!value) {
            return intl.formatMessage({ id: 'batteries_timeline_add_modal_required_value' });
        }

        if (!isFirstDayOfMonth(lenientParse(value))) {
            return intl.formatMessage({ id: 'batteries_timeline_modals_date_invalid_format' });
        }

        if (!siblingValue) return;

        if (type === 'start' && value.getTime() >= siblingValue.getTime())
            return intl.formatMessage({ id: 'batteries_common_modals_date_smaller' });
        if (type === 'end' && value.getTime() <= siblingValue.getTime())
            return intl.formatMessage({ id: 'batteries_common_modals_date_bigger' });
    };

    const validateRequiredField = ({ value }) => {
        if (!value) {
            return intl.formatMessage({ id: 'batteries_timeline_add_modal_required_value' });
        }
    };

    const onFormSubmit = async ({
        startDate,
        endDate,
        value,
        peakPeriodId,
        batteryLevel,
        percentOutage,
        productTypeId,
    }) => {
        const payload = {
            parameter: parameter.value,
            parameterPeriod: {
                startDate: createDateAsUTC(lenientParse(startDate)),
                endDate: createDateAsUTC(lenientParse(endDate)),
                value: Number(value),
            },
            subParameter: {
                peakPeriodId: Number(peakPeriodId) || null,
                productTypeId: Number(productTypeId) || null,
                batteryLevel: batteryLevel,
                percentOutage: percentOutage,
            },
        };

        await rest.checkOverlap(payload);
    };

    const dateFields = formFields.filter((field) => field === 'startDate' || field === 'endDate');
    const percentOutageField = formFields.filter((field) => field === 'percentOutage');
    const batteryLevelField = formFields.filter((field) => field === 'batteryLevel');
    const productTypeField = formFields.filter((field) => field === 'productTypeId');
    const peakPeriodField = formFields.filter((field) => field === 'peakPeriodId');
    const valueField = formFields.filter((field) => field === 'value');

    const hasAdditionalFields =
        (peakPeriodField.length > 0 && batteryLevelField.length > 0) || percentOutageField.length > 0;

    return (
        <>
            <Modal {...rest}>
                <ModalOverlay />

                <ModalContent pt={3}>
                    <ModalHeader pr={12}>
                        <Heading as="h3" variant="h3" textTransform="capitalize">
                            <FormattedMessage
                                id="batteries_timeline_add_modal_heading"
                                values={{ item: parameter.label }}
                            />
                        </Heading>
                    </ModalHeader>

                    <ModalCloseButton mt={4} mr={3} h={6} w={6} />

                    <Formik initialValues={initialFormValues} onSubmit={onFormSubmit} validate={validateForm}>
                        {({ isSubmitting, isValid, dirty }) => (
                            <Form>
                                <ModalBody borderBottom="1px" borderColor="border-secondary" pb={4}>
                                    <SimpleGrid columns={{ xl: 2 }} spacingX={4} spacingY={4} py={3}>
                                        {dateFields.map((field, index) => (
                                            <ParameterFormField
                                                key={index}
                                                fieldName={field}
                                                formHelpers={formHelpers}
                                                ribbonStaticData={ribbonStaticData}
                                            />
                                        ))}
                                    </SimpleGrid>

                                    <SimpleGrid spacingX={4} spacingY={4}>
                                        {peakPeriodField.map((field, index) => (
                                            <ParameterFormField
                                                key={index}
                                                fieldName={field}
                                                formHelpers={formHelpers}
                                                ribbonStaticData={ribbonStaticData}
                                            />
                                        ))}

                                        {productTypeField.map((field, index) => (
                                            <ParameterFormField
                                                key={index}
                                                fieldName={field}
                                                formHelpers={formHelpers}
                                                ribbonStaticData={ribbonStaticData}
                                            />
                                        ))}
                                    </SimpleGrid>

                                    <SimpleGrid columns={{ xl: 2 }} spacingX={4} spacingY={4} py={3}>
                                        {hasAdditionalFields ? (
                                            <>
                                                <Box>
                                                    {batteryLevelField.map((field, index) => (
                                                        <ParameterFormField
                                                            key={index}
                                                            fieldName={field}
                                                            formHelpers={formHelpers}
                                                            ribbonStaticData={ribbonStaticData}
                                                        />
                                                    ))}

                                                    {percentOutageField.map((field, index) => (
                                                        <ParameterFormField
                                                            key={index}
                                                            fieldName={field}
                                                            formHelpers={formHelpers}
                                                            ribbonStaticData={ribbonStaticData}
                                                        />
                                                    ))}
                                                </Box>
                                                <Box>
                                                    {valueField.map((field, index) => (
                                                        <ParameterFormField
                                                            key={index}
                                                            fieldName={field}
                                                            formHelpers={formHelpers}
                                                            ribbonStaticData={ribbonStaticData}
                                                        />
                                                    ))}
                                                </Box>
                                            </>
                                        ) : (
                                            <>
                                                <Spacer />
                                                <Box>
                                                    {valueField.map((field, index) => (
                                                        <ParameterFormField
                                                            key={index}
                                                            fieldName={field}
                                                            formHelpers={formHelpers}
                                                            ribbonStaticData={ribbonStaticData}
                                                        />
                                                    ))}
                                                </Box>
                                            </>
                                        )}
                                    </SimpleGrid>
                                </ModalBody>

                                <ModalFooter justifyContent="stretch" flexWrap="wrap">
                                    <Box ml="auto">
                                        <Button variant="secondary" onClick={rest.onClose}>
                                            <Box as="span" textTransform="capitalize">
                                                <FormattedMessage id="common_cancel" />
                                            </Box>
                                        </Button>

                                        <Button
                                            type="submit"
                                            isLoading={isSubmitting}
                                            isDisabled={!(isValid && dirty)}
                                            ml={3}
                                        >
                                            <FormattedMessage id="batteries_timeline_add_value_modal_save_btn" />
                                        </Button>
                                    </Box>
                                </ModalFooter>
                            </Form>
                        )}
                    </Formik>
                </ModalContent>
            </Modal>
            {rest.periodOverlapModal}
        </>
    );
};

export default AddSubParameterValueModal;
